import React from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import qs from 'qs'
import debounce from 'lodash/debounce'
import SearchProviderContainer from '../../containers/search/SearchProviderContainer'
import { getDocumentType } from '../../utils/search/getDocumentType'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { getRepoforId } from '../../utils/getRepoforId'
import { setLastLocation } from '../routing/LastLocationProvider'
import { getLinkForRepo } from '../../utils/getLinkForRepo'
import { newSet } from '../../utils/newItem'
import { showLiveViewModal } from '../../utils/liveView/showLiveViewModal'
import { showNewRepoModal, showNewSectionModal } from '../../actions/modals'
import { updatePreviewItem } from '../../actions/previewItem'
import { buildHelpSearchUrl } from '../../services/search/local/externalLinksRegistry'
import {
  getRecentSearches,
  recordRecentSearch,
  removeRecentSearch,
} from '../../utils/search/recentSearches'
import SearchAutocomplete from './SearchAutocomplete'
import {
  buildRecentSuggestions,
  getPreviewItemIdForSuggestion,
  isUpgradeModalHash,
  quickActionModalType,
  shortcutHintKeyFor,
  SUGGESTION_CLICK_DELAY,
} from './topNavSearchHelpers'
import {
  trackNavigationUse,
  trackQuickActionUse,
  trackHelpUse,
  trackContentUse,
} from '../../services/search/newSearchAnalytics'

class SearchBar extends React.Component {
  constructor(props) {
    super(props)
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this)
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this)
    this.onSuggestionPreviewClick = this.onSuggestionPreviewClick.bind(this)
    this.onSuggestionPreviewDoubleClick = this.onSuggestionPreviewDoubleClick.bind(this)
    this.onQuickActionClick = this.onQuickActionClick.bind(this)
    this.onQuickActionMouseDown = this.onQuickActionMouseDown.bind(this)
    this.trackHighlight = this.trackHighlight.bind(this)
    this.setPreviewHighlight = this.setPreviewHighlight.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.getSuggestionValue = this.getSuggestionValue.bind(this)
    this.handleCaptureKeyDown = this.handleCaptureKeyDown.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onRecentSearchSelected = this.onRecentSearchSelected.bind(this)
    this.onRecentSearchFill = this.onRecentSearchFill.bind(this)
    this.onDeleteRecentSearch = this.onDeleteRecentSearch.bind(this)
    this.markEnterHandled = this.markEnterHandled.bind(this)
    this.registerRefine = this.registerRefine.bind(this)
    this.getAwaitingQuickActionModal = this.getAwaitingQuickActionModal.bind(this)
    this.getDropdownClosedByCreate = this.getDropdownClosedByCreate.bind(this)
    this.onQuickActionCreated = this.onQuickActionCreated.bind(this)
    this.handleUpgradeHashChange = this.handleUpgradeHashChange.bind(this)
    this.onSuggestionsPointerMove = this.onSuggestionsPointerMove.bind(this)
    // True while the highlight is being driven by arrow keys (reset by pointer
    // movement over the dropdown) — lets trackHighlight tell an arrow-up back to
    // the input from a mouse moving onto the preview pane. See onKeyDown.
    this.navViaKeyboard = false
    // Real-time highlighted row, kept on the instance (not state) so the Enter
    // capture-handler never acts on a stale row — unlike the debounced preview
    // highlight below.
    this.currentHighlight = null
    // Pending single-click timer for previewable rows (see onSuggestionPreviewClick).
    this.suggestionClickTimer = null
    // Set while a quick-action modal (New Pack / New Class) launched from the
    // dropdown is open, so the dropdown query is preserved (not wiped on the
    // modal's focus-steal) and restored on Esc/cancel — see onBlur,
    // componentDidUpdate and onQuickActionCreated.
    this.awaitingQuickActionModal = false
    this.awaitingQuickActionModalType = null
    // Set on a confirmed create so the deferred restore in componentDidUpdate
    // skips reopening the dropdown (New Class fires HIDE_MODAL before onCreated).
    this.suppressQuickActionRestore = false
    // Set on a confirmed create to force the dropdown shut. While the quick-action
    // modal is open the dropdown is kept rendered behind it (alwaysRenderSuggestions),
    // which leaves react-autosuggest's internal "focused" flag set; when the modal
    // closes on success that stale flag would otherwise keep the dropdown open
    // (showing the empty/prefill state). This gates shouldRenderSuggestions so the
    // list stays collapsed until the user genuinely re-focuses or types again.
    this.dropdownClosedByCreate = false
    // The Upgrade-to-Pro modal is hash-driven (not a redux modal). We only treat
    // a hash change as a close once the modal has actually opened — otherwise the
    // brief window between selecting the action and the hash being set would look
    // like an immediate close. See syncUpgradeModal.
    this.upgradeModalWasOpen = false
    // Set by react-autosuggest's onSuggestionSelected (method='enter') which
    // fires BEFORE our input-level onKeyDown. Lets onKeyDown tell apart "Enter
    // acted on a real highlighted row" from "Enter with no real selection →
    // go to the full results page". Reset by onKeyDown after each Enter.
    this.enterHandledBySuggestion = false
    // Stable key of the row currently driving the bottom shortcut hint. Footer
    // rows (e.g. "See all content results") are recreated on every render with
    // no stable objectID, so we guard the hint setState on this key rather than
    // object identity to avoid an infinite render loop.
    this.shortcutHintKey = null
    // Debounce ONLY the preview-pane highlight so fast arrow-scrolling doesn't
    // thrash the preview render; the action highlight (trackHighlight) stays
    // synchronous.
    this.setPreviewHighlight = debounce(this.setPreviewHighlight, 100)
    this.state = {
      value: '',
      highlightedSuggestion: null,
      // Whether a dropdown row is currently highlighted (arrow nav / hover) —
      // drives the right-aligned input hint (Submit ⌘Enter vs a bare Enter;
      // see renderInputComponent).
      hasActiveHighlight: false,
      // The truly-highlighted row (including the "See all content results" /
      // "Search Help Center" footers) used to drive the bottom shortcut hint.
      // Tracked separately from highlightedSuggestion because the preview-pane
      // highlight intentionally skips those footers (see setPreviewHighlight),
      // which would otherwise leave the bottom hint showing the previous row.
      shortcutHintSuggestion: null,
      // Whether the search input currently holds DOM focus — drives the input
      // hint's empty state (⌘K when unfocused, ⌘Enter/nothing when focused; see
      // renderInputComponent).
      isInputFocused: false,
      // Persisted recent-search query history (localStorage is not reactive, so
      // we snapshot it on mount and refresh on focus).
      recentSearches: getRecentSearches(),
    }
  }

  componentDidMount() {
    // Capture phase so this runs BEFORE react-autosuggest's own keydown handler
    // (React 16 binds at document in the bubble phase). Lets us open the preview
    // on plain Enter without autosuggest closing the dropdown or clearing state,
    // so the dropdown is preserved at the same position when the modal closes.
    document.addEventListener('keydown', this.handleCaptureKeyDown, true)
    // The Upgrade-to-Pro modal opens by setting window.location.hash, which
    // fires a hashchange event (react-router doesn't re-render us on a bare hash
    // mutation). Listen so we can re-render to apply keepOpenBehindModal and keep
    // the dropdown alive behind it.
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', this.handleUpgradeHashChange)
    }
  }

  componentDidUpdate(prevProps) {
    // A redux quick-action modal (New Pack / New Class) we launched from the
    // dropdown has closed. If it closed without a confirmed create
    // (Esc/cancel/background — onCreated would have cleared the flag), restore
    // the dropdown for the preserved query by re-focusing the input.
    if (
      this.awaitingQuickActionModal &&
      prevProps.currentModal === this.awaitingQuickActionModalType &&
      this.props.currentModal !== this.awaitingQuickActionModalType
    ) {
      this.awaitingQuickActionModal = false
      this.awaitingQuickActionModalType = null
      this.restoreDropdownAfterModal()
    }
    // The hash-driven Upgrade-to-Pro modal is dismissed with history.push (which
    // removes the hash and re-renders us via react-router but does NOT fire
    // hashchange), so its close is detected here rather than in the hashchange
    // listener. See syncUpgradeModal.
    this.syncUpgradeModal()
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleCaptureKeyDown, true)
    if (typeof window !== 'undefined') {
      window.removeEventListener('hashchange', this.handleUpgradeHashChange)
    }
    if (this.suggestionClickTimer) {
      clearTimeout(this.suggestionClickTimer)
      this.suggestionClickTimer = null
    }
  }

  // Capture-phase Enter handling for the highlighted row. Runs BEFORE
  // react-autosuggest's own Enter handler so we can keep the dropdown open.
  // Plain Enter is the default action here:
  //   - recent-search row → fill the input with the term + run the search
  //   - set/question row   → open the lightweight preview modal
  // Cmd/Ctrl+Enter (all results) is left to react-autosuggest's
  // onSuggestionSelected.
  handleCaptureKeyDown(e) {
    // Once the preview modal is open it owns the keyboard (its own capture
    // listener swallows keys); do nothing here.
    if (this.props.previewItem) return
    // While an IME (e.g. Japanese) is composing, Enter confirms the candidate
    // (kanji) — keyCode 229 / isComposing. Let the browser handle it; don't
    // treat it as a search/preview Enter.
    if (e.isComposing || e.keyCode === 229) return
    if (e.key !== 'Enter' && e.keyCode !== 13) return
    if (e.shiftKey || e.ctrlKey || e.metaKey) return
    if (!e.target || e.target.id !== 'topNavSearchBar') return
    // Use the synchronous highlight (not the debounced preview state) so Enter
    // always acts on the row that's actually highlighted right now.
    const suggestion = this.currentHighlight
    if (!suggestion) return

    // "See all results" footer → don't intercept; let react-autosuggest handle
    // Enter so it navigates to the full results page (instead of opening the
    // last real row's preview).
    if (suggestion.showMore) return

    // Recent-search row → fill the input and run the query, keeping the
    // dropdown open (stop autosuggest from selecting + closing).
    if (suggestion.__recentSearch) {
      e.preventDefault()
      e.stopImmediatePropagation()
      this.fillRecentSearch(suggestion.query)
      return
    }

    // Previewable set/question → open the lightweight preview modal.
    const previewId = getPreviewItemIdForSuggestion(suggestion)
    if (!previewId) return
    // Stop autosuggest from handling this Enter (which would close the dropdown
    // and clear the highlight) and open the lightweight preview instead.
    e.preventDefault()
    e.stopImmediatePropagation()
    // Opening a preview is a content search — record the query.
    recordRecentSearch(this.state.value)
    this.props.updatePreviewItem(previewId, 'searchDropdown')
  }

  // Stash the SearchContext `refine` fn (provided by SearchAutocomplete) so the
  // capture handler above can run a query when filling a recent search.
  registerRefine(refine) {
    this.refine = refine
  }

  // Read the instance flag live (it's not state, so it can't be passed as a
  // plain prop). react-autosuggest calls onSuggestionsClearRequested on the blur
  // triggered when a quick-action modal steals focus — we must keep the live
  // query in that case so the dropdown shows results (not the empty/prefill
  // state) behind the modal.
  getAwaitingQuickActionModal() {
    return this.awaitingQuickActionModal
  }

  // Read live by shouldRenderSuggestions. True only between a confirmed
  // quick-action create and the next genuine focus/type, forcing the dropdown
  // shut despite react-autosuggest's stale internal focus state.
  getDropdownClosedByCreate() {
    return this.dropdownClosedByCreate
  }

  // Fill the input with a recent-search term by dispatching a native input
  // event rather than setState. This routes through react-autosuggest's own
  // onChange (method 'type'), which resets its internal highlight index and
  // keeps the list open — so the next ArrowDown reliably lands on the first
  // result row. (setState alone leaves autosuggest's highlight stale and
  // ArrowDown does nothing.)
  fillRecentSearch(query) {
    if (typeof document === 'undefined') return
    const input = document.getElementById('topNavSearchBar')
    if (!input) {
      // Fallback: no DOM input available — fill via state + refine directly.
      this.setState({ value: query })
      if (this.refine) this.refine(query)
      return
    }
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value',
    )
    if (setter && setter.set) {
      setter.set.call(input, query)
    } else {
      input.value = query
    }
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  onKeyDown(e) {
    // Note that arrow navigation is driving the highlight, so trackHighlight can
    // tell an arrow-up-back-to-the-input (drop the highlight) from a mouse
    // moving onto the preview pane (keep it). Reset by pointer movement over the
    // dropdown (see onSuggestionsPointerMove).
    if (e.keyCode === 38 || e.keyCode === 40) this.navViaKeyboard = true
    // While an IME (e.g. Japanese) is composing, Enter confirms the candidate
    // (kanji) rather than submitting — bail so it doesn't run the search. The
    // commit keydown reports keyCode 229 / nativeEvent.isComposing.
    if (e.keyCode === 229 || (e.nativeEvent && e.nativeEvent.isComposing)) return
    // Escape: react-autosuggest's own handler (which runs first) closes the
    // suggestions list; we additionally drop focus so one Esc both closes the
    // dropdown and de-focuses the input.
    if (e.key === 'Escape' || e.keyCode === 27) {
      const input = document.getElementById('topNavSearchBar')
      if (input && typeof input.blur === 'function') input.blur()
      return
    }
    // react-autosuggest fires onSuggestionSelected (method='enter') BEFORE this
    // handler, but ONLY when it has a real internal highlight. When it does,
    // that path handles the Enter (open / preview / results) and sets the flag
    // below, so we bail. Otherwise (input focused, no real selection — even if a
    // stale hover preview is still showing) we go to the full results page.
    //
    // NB: we must not gate on `this.state.highlightedSuggestion` here — that is
    // the debounced *preview* highlight which is intentionally kept after the
    // pointer leaves a row, so it stays truthy when react-autosuggest has no
    // actual selection. Using it would make Cmd+Enter a no-op from the input.
    if (e.key !== 'Enter' && e.keyCode !== 13) return
    if (this.enterHandledBySuggestion) {
      this.enterHandledBySuggestion = false
      return
    }
    this.goToResultsPageForCurrentQuery()
  }

  // Flag setter handed to SearchAutocomplete so its react-autosuggest
  // onSuggestionSelected can mark that an Enter was consumed by a real
  // highlighted row (see onKeyDown).
  markEnterHandled() {
    this.enterHandledBySuggestion = true
  }

  openHighlightedSuggestion(suggestion, openInNewTab) {
    if (!suggestion) return
    // "See all results" → go to the full results page via goToResultsPageForQuery
    // so the top-nav input is blurred. Otherwise it keeps focus and ArrowDown on
    // the results page re-opens this dropdown instead of moving through results.
    if (suggestion.showMore) {
      trackContentUse({
        source: 'searchDropdown',
        query: suggestion.showMore,
        action: 'seeAllResults',
        itemType: 'query',
      })
      this.goToResultsPageForQuery(suggestion.showMore)
      return
    }
    const query = this.state.value
    // Quick actions that open an in-app modal (New Pack / New Class / Upgrade to
    // Pro) keep the dropdown open behind the modal — showing the live search
    // results, exactly like the preview modal. The query/results are preserved
    // (onBlur and the suggestions-clear are both short-circuited while awaiting)
    // so Esc/cancel/close leaves the dropdown as-is; only a confirmed create /
    // successful upgrade tears it down (onQuickActionCreated / the Stripe-success
    // page reload).
    const modalType = suggestion.__quickAction ? quickActionModalType(suggestion.action) : null
    const isModalQuickAction = !!modalType
    if (isModalQuickAction) {
      // Mark synchronously here (not in the deferred dispatchQuickAction): on a
      // mouse click react-autosuggest blurs the input immediately after this
      // selection (focusInputOnSuggestionClick is false), and onBlur must already
      // see the flag to preserve the query.
      this.awaitingQuickActionModal = true
      this.awaitingQuickActionModalType = modalType
      // The upgrade modal is hash-driven; reset the was-open tracker so a fresh
      // launch isn't mistaken for a close before its hash is set.
      if (modalType === 'UPGRADE_MODAL') this.upgradeModalWasOpen = false
      // Fresh launch — clear any stale suppress flag from a prior create so the
      // Esc/cancel restore isn't accidentally swallowed.
      this.suppressQuickActionRestore = false
    } else {
      // Every other terminal branch below opens/navigates/launches something, so
      // close the dropdown and drop focus from the input. Without this the input
      // keeps focus and react-autosuggest re-reveals the dropdown — either
      // immediately (open a class/folder) or when a launched modal later closes.
      this.dismissDropdown()
    }

    // Record content searches only — opening a library item, a recent item, or
    // the "see all results" footer. Navigation / external / quick-action / help
    // selections are not content searches and are skipped.
    const isContentSelection = !!(
      suggestion.__recent ||
      suggestion.showMore ||
      getDocumentType(suggestion)
    )
    if (isContentSelection) recordRecentSearch(query)

    if (suggestion.__quickAction) {
      // Defer the dispatch to the next task so a modal launched by the action
      // (e.g. "New Pack") registers its window-level keydown listener AFTER the
      // current Enter keydown has finished bubbling. Otherwise that same Enter
      // reaches the freshly-mounted modal and immediately confirms it (creating
      // a "New Pack" with default values).
      const { action } = suggestion
      trackQuickActionUse({
        source: 'searchDropdown', query, action, label: suggestion.label,
      })
      window.setTimeout(() => this.dispatchQuickAction(action), 0)
      return
    }
    if (suggestion.__helpShowMore) {
      trackHelpUse({
        source: 'searchDropdown', query, type: 'helpSearch', label: 'Search Help Center',
      })
      window.open(buildHelpSearchUrl(suggestion.query), '_blank', 'noopener,noreferrer')
      return
    }
    if (suggestion.__external) {
      trackHelpUse({
        source: 'searchDropdown',
        query,
        type: 'externalLink',
        label: suggestion.label,
        url: suggestion.url,
      })
      window.open(suggestion.url, '_blank', 'noopener,noreferrer')
      return
    }
    if (suggestion.__nav) {
      // "Now Playing" is a nav entry without a route — it opens the Live View
      // modal instead of navigating.
      trackNavigationUse({
        source: 'searchDropdown',
        query,
        label: suggestion.label,
        path: suggestion.path,
        action: suggestion.action,
        icon: suggestion.icon,
      })
      if (suggestion.action === 'nowPlaying') {
        showLiveViewModal()
        return
      }
      // Opening a class counts as a content search — record the query so it
      // shows up in recent searches (other nav targets aren't recorded).
      if (suggestion.icon === 'class') recordRecentSearch(query)
      setLastLocation('search')
      if (openInNewTab) {
        window.open(suggestion.path, '_blank', 'noopener,noreferrer')
      } else {
        this.props.history.push(suggestion.path)
      }
      return
    }
    if (suggestion.__recent) {
      const { item, __recentItemType: itemType } = suggestion
      if (item) {
        trackContentUse({
          source: 'searchDropdown', query, action: 'open', itemType, itemId: item.id,
        })
        setLastLocation('search')
        const path = itemType === 'set' ? `/set/${item.id}` : `/question/${item.id}`
        if (openInNewTab) {
          window.open(path, '_blank', 'noopener,noreferrer')
        } else {
          this.props.history.push(path)
        }
      }
      return
    }
    const itemId = suggestion.id || suggestion._id
    const itemType = getDocumentType(suggestion)
    let itemPath
    switch (itemType) {
      case 'repo':
        itemPath = getLinkForRepo(suggestion)
        break
      case 'folder':
        // A folder inside a pack is addressed under that pack's route.
        itemPath = `/library/${itemId}`
        if (suggestion.repo) {
          itemPath = `${getLinkForRepo(getRepoforId(suggestion.repo))}/${itemId}`
        }
        break
      case 'set':
        itemPath = `/set/${itemId}`
        break
      case 'question':
        itemPath = `/question/${itemId}`
        break
      // An unrecognised shape used to fall through to the question route, which
      // would navigate to /question/undefined. Nothing to open, so stop here.
      default:
        return
    }
    trackContentUse({
      source: 'searchDropdown', query, action: 'open', itemType, itemId,
    })
    setLastLocation('search')
    if (openInNewTab) {
      window.open(itemPath, '_blank', 'noopener,noreferrer')
    } else {
      this.props.history.push(itemPath)
    }
  }

  // Close the dropdown and de-focus the search input. Used by every terminal
  // "open" action so the still-focused input doesn't make react-autosuggest
  // re-reveal the dropdown after navigation / after a launched modal closes.
  dismissDropdown() {
    this.currentHighlight = null
    this.shortcutHintKey = null
    // Reset isInputFocused too: on a dropdown-launched create the modal (not the
    // input) held focus, so the blur() below is a no-op and onBlur never fires
    // to clear it — leaving the ⌘K input hint suppressed until a manual
    // focus/blur. Clearing it here restores the hint immediately.
    this.setState({
      value: '', highlightedSuggestion: null, shortcutHintSuggestion: null, isInputFocused: false,
    })
    if (typeof document === 'undefined') return
    const input = document.getElementById('topNavSearchBar')
    if (input && typeof input.blur === 'function') input.blur()
  }

  goToResultsPageForCurrentQuery() {
    this.goToResultsPageForQuery(this.state.value || '')
  }

  goToResultsPageForQuery(rawQuery) {
    const query = rawQuery || ''
    // Opening the results page for a query is a content search — record it.
    recordRecentSearch(query)
    const search = query ? `?${qs.stringify({ query })}` : ''
    this.props.history.push(`/searchresults/${search}`)
    this.setState({ value: '', highlightedSuggestion: null })
    // Drop focus from the search input so the results page (which listens
    // for ArrowUp/ArrowDown/Enter on document) can take keyboard control
    // immediately — the global handler ignores events whose target is an
    // editable field, so leaving focus on the input would swallow them.
    if (
      typeof document !== 'undefined' &&
      document.activeElement &&
      typeof document.activeElement.blur === 'function'
    ) {
      document.activeElement.blur()
    }
  }

  getSuggestionValue(suggestion) {
    // Recent-search rows fill the input with their query; every other row keeps
    // the user's typed text (selection opens/navigates rather than filling).
    if (suggestion && suggestion.__recentSearch) return suggestion.query
    return this.state.value
  }

  onChange(event, { newValue, method }) {
    // Arrow navigation (method 'up'/'down') also fires onChange with the
    // highlighted suggestion's getSuggestionValue. We never want arrowing to
    // rewrite the query text (it would, e.g., turn the empty state into a live
    // query when landing on a recent-search row), so ignore those methods.
    if (method === 'up' || method === 'down') return
    // While a quick-action modal is open behind the dropdown (notably the
    // hash-driven Upgrade-to-Pro modal, where the search input keeps focus),
    // react-autosuggest's Escape handler fires onChange with an empty value to
    // "clear" the input — because alwaysRenderSuggestions keeps its list open,
    // its willCloseSuggestions branch is skipped. Ignore it so the preserved
    // query survives the modal and can be restored on close.
    if (method === 'escape' && this.awaitingQuickActionModal) return
    // Clear the highlighted suggestion when the user edits the query text so a
    // stale preview from a previous query doesn't linger and Enter reliably
    // goes to the full results page.
    const next = { value: newValue }
    if (method === 'type') {
      // Typing re-engages the dropdown after a post-create close.
      this.dropdownClosedByCreate = false
      next.highlightedSuggestion = null
      // Editing the query drops the highlight, so Enter reliably goes to the
      // full results page rather than acting on a now-stale highlighted row.
      // Also reverts the input hint from "Submit ⌘Enter" back to a bare Enter.
      next.hasActiveHighlight = false
      next.shortcutHintSuggestion = null
      this.shortcutHintKey = null
      this.currentHighlight = null
      // Typing means the user isn't mid quick-action-modal flow; clear any
      // pending flag left by a mousedown that never became a click (drag-off).
      this.awaitingQuickActionModal = false
      this.awaitingQuickActionModalType = null
    }
    this.setState(next)
  }

  onFocus() {
    // A genuine focus clears the post-create close gate so the dropdown behaves
    // normally again (only our own suppressed restore could refocus, so this
    // never fires from the modal teardown).
    this.dropdownClosedByCreate = false
    // Refresh the recent-search snapshot on focus so newly recorded queries
    // (from this or another tab) appear in the empty-state dropdown.
    this.setState({ recentSearches: getRecentSearches(), isInputFocused: true })
  }

  onRecentSearchSelected(query) {
    // Cmd/Ctrl+Enter on a recent search runs the query and navigates to the
    // full results page.
    this.goToResultsPageForQuery(query)
  }

  onRecentSearchFill(query) {
    // Plain Enter / click on a recent search fills the input with the term so
    // the dropdown shows live results for it. Filling via a native input event
    // (see fillRecentSearch) resets autosuggest's highlight so ArrowDown then
    // moves to the first result row.
    this.fillRecentSearch(query)
  }

  onDeleteRecentSearch(query) {
    // Remove a recent search (X button) and refresh the snapshot so the row
    // disappears immediately.
    this.setState({ recentSearches: removeRecentSearch(query) })
  }

  onBlur(event) {
    // While a quick-action modal launched from the dropdown is open, keep the
    // query so it can be restored if the modal is dismissed (the modal steals
    // focus, which would otherwise wipe it here).
    if (this.awaitingQuickActionModal) return
    // A tab/window switch blurs the input but leaves it as document.activeElement.
    // In that case keep the typed query + highlight so nothing is lost on return.
    if (event && event.target && document.activeElement === event.target) return
    // Drop the highlight on blur so re-focusing the input and pressing Enter
    // goes to the full results page rather than re-opening a stale highlight.
    this.currentHighlight = null
    this.shortcutHintKey = null
    this.setState({
      value: '',
      highlightedSuggestion: null,
      shortcutHintSuggestion: null,
      hasActiveHighlight: false,
      isInputFocused: false,
    })
  }

  onSuggestionsClearRequested() {
    this.setState({})
  }

  // Pointer moved over the dropdown — the highlight is now mouse-driven, so
  // clear the keyboard-nav flag. This keeps a mouse moving onto the preview pane
  // (which unhighlights the list) from being mistaken for an arrow-up back to
  // the input (see trackHighlight).
  onSuggestionsPointerMove() {
    this.navViaKeyboard = false
  }

  // Synchronously track the truly-highlighted row (including the "see all
  // results" footer) so the Enter capture-handler always acts on the current
  // row, then update the preview pane via the debounced setter.
  trackHighlight({ suggestion }) {
    // Drive the input hint off the live highlight state (arrow nav / hover):
    // a highlighted row flips the hint to "Submit ⌘Enter", no highlight shows a
    // bare Enter. Updated even on unhighlight (suggestion null), unlike the
    // preview state below which intentionally lingers.
    const nextHasHighlight = !!suggestion
    if (nextHasHighlight !== this.state.hasActiveHighlight) {
      this.setState({ hasActiveHighlight: nextHasHighlight })
    }
    if (!suggestion) {
      // Highlight cleared. If the user arrowed back up to the input (keyboard)
      // and no modal is holding the dropdown open, drop the persisted highlight
      // so the top row isn't left highlighted once focus is back in the input.
      // A mouse moving off the list (e.g. onto the preview pane) also clears the
      // highlight — keep the last real row then so the preview stays stable.
      const modalHeld = !!this.props.previewItem || this.awaitingQuickActionModal
      if (this.navViaKeyboard && !modalHeld) {
        this.currentHighlight = null
        this.shortcutHintKey = null
        this.setState({
          highlightedSuggestion: null,
          shortcutHintSuggestion: null,
          hasActiveHighlight: false,
        })
      }
      return
    }
    this.currentHighlight = suggestion
    // Drive the bottom shortcut hint off the true highlighted row — including
    // the "See all content results" / "Search Help Center" footers, which the
    // preview-pane highlight below intentionally skips. Guard on a stable key
    // (not object identity) because the footer row is recreated every render
    // with no objectID; a reference check would setState endlessly.
    const hintKey = shortcutHintKeyFor(suggestion)
    if (hintKey !== this.shortcutHintKey) {
      this.shortcutHintKey = hintKey
      this.setState({ shortcutHintSuggestion: suggestion })
    }
    this.setPreviewHighlight({ suggestion })
  }

  // Debounced preview-pane highlight. The "See all content results" footer
  // (showMore) is treated like any other non-previewable row: it becomes the
  // highlighted suggestion so shouldShowPreviewFor returns false and the preview
  // column hides — matching the "Search Help Center" (__helpShowMore) row.
  setPreviewHighlight({ suggestion }) {
    if (!suggestion) return
    if (suggestion === this.state.highlightedSuggestion) return
    this.setState({ highlightedSuggestion: suggestion })
  }

  // Fired by a quick-action modal on a confirmed create (success). Tears down
  // the preserved dropdown — the inverse of the Esc/cancel path, which restores
  // it (see componentDidUpdate).
  onQuickActionCreated() {
    this.awaitingQuickActionModal = false
    this.awaitingQuickActionModalType = null
    // A confirmed create. New Class's HIDE_MODAL (dispatched inside the
    // newSection action) fires BEFORE this callback, so componentDidUpdate may
    // have already scheduled a deferred restore treating it as a cancel. This
    // flag (checked in that deferred task, which runs after this microtask)
    // suppresses the reopen so the dropdown stays closed on success.
    this.suppressQuickActionRestore = true
    // Force the dropdown shut. react-autosuggest still thinks the input is focused
    // (its blur was swallowed while alwaysRenderSuggestions held the list open
    // behind the modal), so on the modal close it would otherwise keep rendering
    // the list. This gate (read by shouldRenderSuggestions) overrides that until
    // the next genuine focus/type. Reset in onFocus / onChange.
    this.dropdownClosedByCreate = true
    // Clear the live query as well so there's nothing to show even once the gate
    // lifts (while awaiting we deliberately preserved it for the Esc/cancel case).
    if (this.refine) this.refine('')
    this.dismissDropdown()
  }

  // Deferred re-focus that reopens the dropdown after a quick-action modal is
  // dismissed without a confirmed create (Esc/cancel/background). Deferred to a
  // macrotask for two reasons:
  //   1. Some modals (New Class is a redux-form with several fields) move focus
  //      to <body> as they unmount AFTER the lifecycle that calls this; the
  //      deferred re-focus runs once that settles and reliably reopens it.
  //   2. Lets a confirmed-create signal arrive first. New Class's `newSection`
  //      action dispatches HIDE_MODAL BEFORE onQuickActionCreated runs, so the
  //      flag isn't cleared yet when componentDidUpdate schedules this.
  //      onQuickActionCreated runs as a microtask (before this macrotask) and
  //      sets suppressQuickActionRestore, so a successful create isn't mistaken
  //      for a cancel and won't reopen.
  restoreDropdownAfterModal({ reopenQuery = false } = {}) {
    if (typeof document === 'undefined') return
    window.setTimeout(() => {
      if (this.suppressQuickActionRestore) {
        this.suppressQuickActionRestore = false
        return
      }
      const input = document.getElementById('topNavSearchBar')
      if (input && typeof input.focus === 'function') input.focus()
      // The hash-driven upgrade overlay can leave the search input focused, so a
      // bare focus() won't fire onFocus to reopen react-autosuggest's list (it
      // collapsed when the quick action was selected) or re-run the preserved
      // query — and onSuggestionsClearRequested has meanwhile cleared the context
      // refinement. Re-dispatch the query as a native input event to reliably
      // reopen the dropdown with its results. (Redux modals steal focus, so their
      // plain refocus already reopens + refetches — hence this is opt-in so it
      // doesn't disturb their retained highlight.)
      if (reopenQuery && this.state.value) this.fillRecentSearch(this.state.value)
    }, 0)
  }

  // Reconcile the hash-driven Upgrade-to-Pro modal with the preserved dropdown.
  // Called from the hashchange listener (open / landing→payment step) and from
  // componentDidUpdate (the history.push dismiss re-renders us). Mirrors the New
  // Pack / New Class flow: keep the dropdown alive while the modal is open and
  // restore it on a plain close. A confirmed upgrade never reaches the close
  // branch because Stripe success reloads the page (return_url redirect) — the
  // fresh mount leaves the dropdown shut, which is the desired "close on success".
  syncUpgradeModal() {
    const awaitingUpgrade = this.awaitingQuickActionModal &&
      this.awaitingQuickActionModalType === 'UPGRADE_MODAL'
    if (!awaitingUpgrade) return
    const isOpen = typeof window !== 'undefined' && isUpgradeModalHash(window.location.hash)
    if (isOpen) {
      this.upgradeModalWasOpen = true
      return
    }
    // Not open yet and never opened → still in the gap between selecting the
    // action and the hash being set; don't treat it as a close.
    if (!this.upgradeModalWasOpen) return
    // Was open, now closed (Esc / close icon / background click) → restore.
    this.upgradeModalWasOpen = false
    this.awaitingQuickActionModal = false
    this.awaitingQuickActionModalType = null
    // reopenQuery: the upgrade overlay doesn't steal focus, so re-run the query
    // to reopen the dropdown with its preserved results (see restoreDropdownAfterModal).
    this.restoreDropdownAfterModal({ reopenQuery: true })
  }

  // hashchange fires when the upgrade modal opens (window.location.hash set) and
  // on its internal landing→payment step. Re-render so keepOpenBehindModal takes
  // effect, and reconcile the open/close state. Ignored unless we launched the
  // upgrade modal from the dropdown.
  handleUpgradeHashChange() {
    if (this.awaitingQuickActionModalType !== 'UPGRADE_MODAL') return
    this.syncUpgradeModal()
    this.forceUpdate()
  }

  // Dispatcher for quick-action selections. Centralised so the registry's
  // `action` token is the single source of truth — add a new quick action by
  // appending to STATIC_QUICK_ACTIONS and adding one arm here.
  dispatchQuickAction(action) {
    switch (action) {
      case 'newSet':
        newSet()
        return true
      case 'newRepo':
        // awaitingQuickActionModal* is set synchronously in
        // openHighlightedSuggestion (needed before the mouse-click blur).
        if (this.props.showNewRepoModal) this.props.showNewRepoModal(this.onQuickActionCreated)
        return true
      case 'newSection':
        if (this.props.showNewSectionModal) this.props.showNewSectionModal(this.onQuickActionCreated)
        return true
      case 'upgradePro':
        // Hash-driven modal handled by UpgradeContainer (same trigger the
        // RepoInvitationBanner / account upgrade buttons use).
        window.location.hash = 'upgrade'
        return true
      default:
        return false
    }
  }

  onSuggestionSelected(event, { suggestion, method }) {
    // react-autosuggest fires this BEFORE our onKeyDown, and only when a
    // suggestion is highlighted. Branch on modifier keys carried on the event:
    //   Ctrl/Cmd+Enter → full results page (all results)
    //   plain Enter      → preview modal (set/question) — intercepted earlier in
    //                     the capture handler, so plain Enter only reaches here
    //                     for non-previewable rows (nav/external/quick-action/
    //                     pack/folder/help/see-all), where it opens/navigates.
    if (method === 'enter') {
      // Ignore the Enter that confirms an IME composition (kanji selection) so
      // it doesn't act on the highlighted row / navigate.
      if (event.keyCode === 229 || (event.nativeEvent && event.nativeEvent.isComposing)) return
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
        // Cmd/Ctrl+Enter ("Submit") always searches the entered string and
        // opens the full results page — never the highlighted row the user is
        // standing on. The only exception is the empty-state recent ITEM rows
        // (no typed query), where it falls back to the item's name so the
        // shortcut still does something useful.
        const typed = (this.state.value || '').trim()
        if (!typed && suggestion && suggestion.__recent && suggestion.item) {
          const q = suggestion.__recentItemType === 'set' ?
            suggestion.item.name :
            getQuestionBodyLabel(suggestion.item)
          this.goToResultsPageForQuery(q)
          return
        }
        this.goToResultsPageForCurrentQuery()
        return
      }
      // Shift+Enter is no longer a shortcut — ignore it so it doesn't open the
      // item (previewable rows open the preview on plain Enter; the preview's
      // own Enter then opens the item).
      if (event.shiftKey) {
        event.preventDefault()
        return
      }
      // Plain Enter on a non-previewable row → open / navigate. (Plain Enter on
      // a previewable row is intercepted earlier in handleCaptureKeyDown.)
      this.openHighlightedSuggestion(suggestion, false)
      return
    }

    // Mouse click path (method === 'click') — open / run the item.
    // NB: previewable rows (set/question/recent set-question) handle their own
    // clicks in the DOM (see previewClickHandlers) and stop propagation, so this
    // branch only runs for non-previewable rows (packs/folders/nav/external/
    // quick-action/help/see-all), which always open/execute on click.
    this.openHighlightedSuggestion(suggestion, false)
  }

  // Single click on a previewable row → open the lightweight preview modal.
  // Deferred so a double click (→ open the item) can cancel it first.
  onSuggestionPreviewClick(suggestion) {
    const previewId = getPreviewItemIdForSuggestion(suggestion)
    if (!previewId) {
      this.openHighlightedSuggestion(suggestion, false)
      return
    }
    if (this.suggestionClickTimer) clearTimeout(this.suggestionClickTimer)
    this.suggestionClickTimer = window.setTimeout(() => {
      this.suggestionClickTimer = null
      // Opening a preview is a content search — record the query. We keep the
      // dropdown open (no dismissDropdown) so it stays behind the preview modal
      // and reappears when the modal closes (item 9).
      recordRecentSearch(this.state.value)
      this.props.updatePreviewItem(previewId, 'searchDropdown')
    }, SUGGESTION_CLICK_DELAY)
  }

  // Mouse-click on a quick-action row. The row stops its click from reaching
  // react-autosuggest (which would collapse the dropdown on selection), so we
  // run the action ourselves — same flag/dismiss bookkeeping as the keyboard
  // path so a modal-opening action keeps the dropdown alive behind the modal.
  onQuickActionClick(suggestion) {
    this.openHighlightedSuggestion(suggestion, false)
  }

  // Mousedown on a quick-action row — fires before the click and before the
  // blur react-autosuggest triggers around the selection. For a modal-opening
  // quick action (New Pack / New Class / Upgrade to Pro) we set the
  // preserve-the-dropdown flag here so onBlur keeps the query alive; the click
  // then opens the modal via onQuickActionClick → openHighlightedSuggestion
  // (which sets it again, harmlessly). The keyboard path sets the same flag
  // before the modal opens.
  onQuickActionMouseDown(suggestion) {
    if (!suggestion || !suggestion.__quickAction) return
    const modalType = quickActionModalType(suggestion.action)
    if (!modalType) return
    this.awaitingQuickActionModal = true
    this.awaitingQuickActionModalType = modalType
    // The upgrade modal is hash-driven; reset the was-open tracker so a fresh
    // launch isn't mistaken for a close before its hash is set.
    if (modalType === 'UPGRADE_MODAL') this.upgradeModalWasOpen = false
    // Fresh launch — clear any stale suppress flag from a prior create.
    this.suppressQuickActionRestore = false
  }

  // Double click on a previewable row → cancel the pending preview and open the
  // item directly.
  onSuggestionPreviewDoubleClick(suggestion) {
    if (this.suggestionClickTimer) {
      clearTimeout(this.suggestionClickTimer)
      this.suggestionClickTimer = null
    }
    this.openHighlightedSuggestion(suggestion, false)
  }

  render() {
    const {
      value, highlightedSuggestion, recentSearches, hasActiveHighlight, isInputFocused,
      shortcutHintSuggestion,
    } = this.state
    const {
      recentActivities, sets, questions, currentModal, service,
    } = this.props
    // The "Upgrade to Pro" quick action only makes sense for free users. Hide
    // it for anyone already on Pro (service.product === 'plus', incl. trial /
    // canceled / pass) — matching the app's other upgrade CTAs which show only
    // when service.product === 'free'.
    const hideUpgradeQuickAction = !(service && service.product === 'free')
    const inputProps = {
      placeholder: 'Search',
      value,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      onKeyDown: this.onKeyDown,
      // While the preview modal is open the input keeps focus so the dropdown
      // stays rendered behind it — but that lets an active IME (e.g. Japanese)
      // compose text into the field when the user presses a preview shortcut
      // (p/q/e). A read-only input keeps focus without accepting IME input, so
      // the keydown reaches the preview modal's capture handler instead.
      readOnly: !!this.props.previewItem,
    }

    const recentSuggestions = buildRecentSuggestions(recentActivities, sets, questions, 5)

    // Keep the dropdown rendered behind a quick-action modal (New Pack / New
    // Class / Upgrade to Pro) that was launched FROM the dropdown, so it stays
    // visible with the live search results — matching the preview modal. Guarded
    // on the awaiting flag so the same modals opened elsewhere (e.g. Library
    // buttons, help menu) don't pop the search dropdown open. The upgrade modal
    // is hash-driven (no redux currentModal), so it's keyed off the modal type
    // the flag records instead — see syncUpgradeModal for its lifecycle.
    const keepOpenBehindModal = this.awaitingQuickActionModal && (
      currentModal === 'NEW_REPO_MODAL' ||
      currentModal === 'NEW_SECTION_MODAL' ||
      this.awaitingQuickActionModalType === 'UPGRADE_MODAL'
    )

    return (
      <SearchProviderContainer isTopNav>
        <SearchAutocomplete
          keepOpenBehindModal={keepOpenBehindModal}
          hideUpgradeQuickAction={hideUpgradeQuickAction}
          getAwaitingQuickActionModal={this.getAwaitingQuickActionModal}
          getDropdownClosedByCreate={this.getDropdownClosedByCreate}
          getSuggestionValue={this.getSuggestionValue}
          inputProps={inputProps}
          onSuggestionSelected={this.onSuggestionSelected}
          onRecentSearchSelected={this.onRecentSearchSelected}
          onRecentSearchFill={this.onRecentSearchFill}
          onDeleteRecentSearch={this.onDeleteRecentSearch}
          onSuggestionPreviewClick={this.onSuggestionPreviewClick}
          onSuggestionPreviewDoubleClick={this.onSuggestionPreviewDoubleClick}
          onQuickActionClick={this.onQuickActionClick}
          onQuickActionMouseDown={this.onQuickActionMouseDown}
          markEnterHandled={this.markEnterHandled}
          registerRefine={this.registerRefine}
          value={value}
          onSuggestionHighlighted={this.trackHighlight}
          onSuggestionsPointerMove={this.onSuggestionsPointerMove}
          recentSearches={recentSearches}
          recentSuggestions={recentSuggestions}
          highlightedSuggestion={highlightedSuggestion}
          shortcutHintSuggestion={shortcutHintSuggestion}
          hasActiveHighlight={hasActiveHighlight}
          isInputFocused={isInputFocused}
        />
      </SearchProviderContainer>
    )
  }
}

function mapStateToProps(state) {
  return {
    recentActivities: state.recentActivities,
    sets: state.sets,
    questions: state.questions,
    previewItem: state.previewItem,
    currentModal: state.modals.currentModal,
    service: state.service,
  }
}

const mapDispatchToProps = {
  showNewRepoModal,
  showNewSectionModal,
  updatePreviewItem,
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SearchBar))
